// covered-by: packages/schemas/src/__tests__/knowledge-rule-schema.spec.ts
import { z } from "zod";

import {
  KnowledgeScopeSource,
  knowledgeRuleCitationSchema,
  knowledgeScopeSchema,
} from "./knowledge";

const timeStamps = z.object({
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * A knowledge rule. Rules are created ONLY by the rembrandt extraction /
 * synthesis workflow — there is no manual create/edit path. Admin mutations
 * are limited to status transitions (promote/reject/archive) and scope edits.
 *
 * Suggestions are rules with `status=suggestion` (low-trust origin with
 * enough confidence); promotion is a status transition so the _id, citations,
 * and audit chain survive.
 *
 * Raw extractions live in the rembrandt-owned, snake_case, append-only
 * `knowledge_extractions` collection (no mongoose model — christo never
 * reads it this phase); `citations[].extractionId` back-references it.
 */
export enum KnowledgeRuleStatus {
  SUGGESTION = "suggestion",
  ACTIVE = "active",
  ARCHIVED = "archived",
  REJECTED = "rejected",
}

export enum KnowledgeRuleOrigin {
  HIGH_TRUST = "high_trust",
  LOW_TRUST = "low_trust",
}

export enum KnowledgeRuleCategory {
  CLAIM = "claim",
  BRAND = "brand",
  CHECKLIST = "checklist",
  POLICY = "policy",
  OTHER = "other",
}

/** Declared now; incremented by the later review-integration phase. */
export const knowledgeRuleUsageSchema = z.object({
  reviewCount: z.coerce.number().int().min(0).default(0),
  lastUsedInReviewAt: z.coerce.date().nullable().optional(),
});

export const knowledgeRuleSchema = z
  .object({
    _id: z.string(),
    enterpriseId: z.string(),
    title: z.string().optional(),
    content: z.string().min(1),
    category: z.nativeEnum(KnowledgeRuleCategory),
    status: z.nativeEnum(KnowledgeRuleStatus),
    origin: z.nativeEnum(KnowledgeRuleOrigin),
    confidence: z.coerce.number().min(0).max(1),
    scope: knowledgeScopeSchema.default({}),
    scopeSource: z
      .nativeEnum(KnowledgeScopeSource)
      .default(KnowledgeScopeSource.NONE),
    // Citations are MANDATORY: every rule links to its exact source
    // location(s). Ordered — citations[0] is primary (most trustworthy).
    citations: z.array(knowledgeRuleCitationSchema).min(1),
    usage: knowledgeRuleUsageSchema.default({ reviewCount: 0 }),
    extraction: z
      .object({
        workflowId: z.string().optional(),
        runId: z.string().optional(),
        extractedAt: z.coerce.date().optional(),
        model: z.string().optional(),
      })
      .optional(),
    statusChangedBy: z.string().nullable().optional(),
    statusChangedAt: z.coerce.date().nullable().optional(),
    confirmationCount: z.coerce.number().int().min(0).default(0),
    contradictionCount: z.coerce.number().int().min(0).default(0),
    supersededBy: z.string().nullable().optional(),
  })
  .merge(timeStamps);

export type KnowledgeRule = z.infer<typeof knowledgeRuleSchema>;
export type KnowledgeRuleUsage = z.infer<typeof knowledgeRuleUsageSchema>;
